import { SportsApiClient, LeaguesApiClient, EventsApiClient, MarketsApiClient, BreedsApiClient, RacesApiClient, SearchApiClient, RegionsApiClient, TracksApiClient } from "./BFFGatewayClient";
import { GlobalConfig } from "../configuration";
import { BFFGatewayConfiguration } from "./BFFGatewayConfiguration";
import { AjaxService } from "sb-helpers";
/**
 * BFFGateway class provides interface for communication between BFF service and blocks.
 *
 * sbBffIntegration.GlobalConfig object needs to be inited before using BFFGateway
 * as base URL for BFF is present from config.
 *
 * example usage:
 * var gateway = new sbBffIntegration.BFFGateway();
 * gateway.getSports({ ... });
 *
 */
/* istanbul ignore next */
export class BFFGateway {
    constructor() {
        this.config = new BFFGatewayConfiguration({
            accessToken: GlobalConfig
                .getAuthorization()
                .token,
            locale: GlobalConfig.apiConfigs.locale,
            basePath: GlobalConfig.apiConfigs.bffApiUrl
        });
        let service = new AjaxService();
        this.sportsApiClient = new SportsApiClient(this.config, service);
        this.leaguesApiClient = new LeaguesApiClient(this.config, service);
        this.eventsApiClient = new EventsApiClient(this.config, service);
        this.marketsApiClient = new MarketsApiClient(this.config, service);
        this.breedsApiClient = new BreedsApiClient(this.config, service);
        this.racesApiClient = new RacesApiClient(this.config, service);
        this.regionsApiClient = new RegionsApiClient(this.config, service);
        this.tracksApiClient = new TracksApiClient(this.config, service);
        this.searchApiClient = new SearchApiClient(this.config, service);
    }
    /**
     * Get BFF request configuration from GlobalConfig
     * @return token from GlobalConfig
     */
    getGlobalConfigToken() {
        return GlobalConfig
            .getAuthorization()
            .token;
    }
    //builder constructor for BffGateway with blockId parameter
    /**
     *
     * @param blockId
     */
    withBlockId(blockId) {
        this.config.blockId = blockId;
        return this;
    }
    //Event endpoints
    /**
     * Request events by coupon id
     * @param request EventsByCouponIdRequest
     */
    getEventsByCouponId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByCouponId(request);
    }
    /**
     * Request events by sport id
     * @param request EventsBySportIdRequest
     */
    getEventsBySportId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBySportId(request);
    }
    /**
     * Request events by event ids
     * @param request EventsByEventIdRequest
     */
    getEventsByEventId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByEventsId(request);
    }
    /**
     *
     * @param request EventsByRegionId
     */
    getEventsByRegionId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByRegionId(request);
    }
    /**
     * Request events by league ids
     * @param request EventsByLeagueIdRequest
     */
    getEventsByLeagueId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByLeaguesId(request);
    }
    /**
     * Request outrights by league ids
     * @param request OutrightEventsByLeagueIdRequest
     */
    getOutrightsByLeagueId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getFromOutrightByLeagueId(request);
    }
    /**
     * Request events by event ids or by league ids
     * @param request EventsByEventIdOrByLeagueIdRequest
     */
    getEventsByEventIdOrByLeagueId(request) {
        this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken());
        return this
            .eventsApiClient
            .getByEventIdOrByLeagueId(request);
    }
    /**
     * Request events from top leagues by sport id
     * @param request EventsFromTopLeaguesBySportIdRequest
     */
    getEventsFromTopLeaguesBySportId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getFromTopLeaguesBySportId(request);
    }
    /**
     * Request personalized live events based on event id
     * @param request PersonalizedLiveEventsRequest
     */
    getPersonalizedEvents(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getPersonalizedEvents(request);
    }
    /**
     * Request events by eventIds, marketIds and selectionIds
     * @param request EventsByEventIdForBetSlipRequest
     */
    getEventsByIdForBetslip(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByIdForBetslip(request);
    }
    /**
     * Request personalized events by sport ids
     * @param {PersonalizedEventsPerSportRequest} request
     */
    getPersonalizedPreLiveEventsBySportId(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getPersonalizedPreLiveEventsBySportId(request);
    }
    /**
     * Request personalized prelive events
     * @param {PersonalizedPreLiveEventsRequest} request
     */
    getPersonalizedPreLiveEvents(request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getPersonalizedPreLiveEvents(request);
    }
    //Markets endpoints
    /**
     * Request markets by event id
     * @param request MarketsByEventIdRequest
     */
    getMarketsByEventId(request) {
        return this
            .marketsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByEventId(request);
    }
    //Leagues endpoints
    /**
     * Request all leagues by sport ids
     * @param request LeaguesBySportIdRequest
     */
    getLeaguesBySportId(request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBySportId(request);
    }
    //Leagues endpoints
    /**
     * Request all leagues by sport ids
     * @param request LeaguesByLeagueIdRequest
     */
    getLeaguesByLeagueId(request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getLeaguesByLeagueId(request);
    }
    /**
     * Request all top leagues by sport id
     * @param request TopLeaguesBySportIdRequest
     */
    getTopLeaguesBySportId(request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getTopLeaguesBySportId(request);
    }
    //Sports endpoints
    /**
     * Request all sports
     * @param request SportsRequest
     */
    getSports(request) {
        return this
            .sportsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getAllSports(request);
    }
    /**
     * Get sports grouped by Date from BFF
     * @param {request} SportsGroupedByDateRequest
     * @throws {RequiredError}
     * @memberof SportsApiClient
     */
    getSportsGroupedByDate(request) {
        return this.sportsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getSportsGroupedByDate(request);
    }
    /**
     * Get all personalized sports
     * @param {PersonalizedSportsRequest} request
     */
    getPersonalizedPreLiveSports(request) {
        return this
            .sportsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getPersonalizedPreLiveSports(request);
    }
    /**
     * Fetch events count by tag from BFF
     * @param {request} EventsCountByTagRequest
     * @throws {RequiredError}
     * @memberof EventsApiClient
     */
    getCountByTag(request) {
        this.sportsApiClient.refreshToken(this.getGlobalConfigToken());
        return this.eventsApiClient.getCountByTag(request);
    }
    /**
     * Request all breeds
     */
    getBreeds() {
        return this.breedsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBreeds();
    }
    //Races endpoints
    /**
      * Get a race by race id from BFF
      * @param {string} raceId id of a race
      * @throws {RequiredError}
      * @memberof RacesApiClient
      */
    getRacesById(raceId) {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getById(raceId);
    }
    /**
     * Get races by league ids from BFF
     * @param {string[]} leagueIds leagueIds to get races from
     * @param {number} paginationTop index of last race
     * @param {number} paginationSkip number races to skip
     * @throws {RequiredError}
     * @memberof RacesApiClient
     */
    getRaces(request) {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRaces(request);
    }
    /**
     * Get all races with no participants and tote details from BFF
     * @throws {RequiredError}
     * @memberof RacesApiClient
     */
    getRacesCompact() {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRacesCompact();
    }
    /**
     * Get all regions from BFF
     * @throws {RequiredError}
     * @memberof RegionsApiClient
     */
    getRegions() {
        return this.regionsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRegions();
    }
    /**
     * Get all tracks from BFF
     * @throws {RequiredError}
     * @memberof RegionsApiClient
     */
    getTracks() {
        return this.tracksApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getTracks();
    }
    /**
     * Search events and leagues from BFF
     * @throws {RequiredError}
     * @memberof SearchApiClient
     */
    search(query, sportIds) {
        return this.searchApiClient
            .refreshToken(this.getGlobalConfigToken())
            .search(query, sportIds);
    }
}
//# sourceMappingURL=BFFGateway.js.map